<template>
  <div class="p-fluid grid formgrid">
    <!-- Input for Temporary Item Name -->
    <div class="field col-12 md:col-6">
      <label for="tempName">Temporary Item Name</label>
      <InputText
        id="tempName"
        v-model="form.name"
        placeholder="Enter item name"
        fluid
      />
    </div>

    <!-- Input for Temporary Quantity -->
    <div class="field col-12 md:col-6">
      <label for="tempQty">Quantity</label>
      <InputNumber
        id="tempQty"
        v-model="form.quantity"
        placeholder="Quantity"
        input-id="integeronly"
        fluid
      />
    </div>

    <!-- Input for Temporary Price -->
    <div class="field col-12 md:col-6">
      <label for="tempPrice">Price per Unit</label>
      <InputNumber
        id="tempPrice"
        v-model="form.price"
        placeholder="Price"
        input-id="integeronly"
        fluid
        mode="currency"
        currency="GBP"
      />
    </div>

    <!-- Input for Days -->
    <div class="field col-12 md:col-6">
      <label for="tempDays">Days</label>
      <InputNumber
        id="tempDays"
        v-model="form.days"
        placeholder="Days"
        input-id="integeronly"
        fluid
      />
    </div>

    <!-- Input for Discount -->
    <div class="field col-12 md:col-6">
      <label for="tempDiscount">Discount (%)</label>
      <InputNumber
        id="tempDiscount"
        v-model="form.discount"
        placeholder="Discount"
        input-id="integeronly"
        fluid
      />
    </div>

    <!-- Calculated Cost Display -->
    <div class="field col-12 md:col-6">
      <label>Total Cost</label>
      <div
        class="p-inputtext p-component"
        style="background-color: #f8f9fa; color: #495057;"
      >
        £{{ calculatedCost.toFixed(2) }}
      </div>
    </div>

    <!-- Save Button -->
    <div class="col-12">
      <Button
        label="Save Temporary Item"
        icon="pi pi-check"
        :loading="isLoading"
        @click="saveTempItem"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { addEquipmentListContent } from '@/services/projectService';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';

// Toast service
const toast = useToastService();

// Define the form object with the correct properties
const form = ref({
    name: '',
    quantity: 1,
    price: 0,
    days: 3,
    discount: 0
});

const isLoading = ref(false);

// Calculate total cost based on quantity, price, days, and discount
const calculatedCost = computed(() => {
    const subtotal = form.value.quantity * form.value.price * form.value.days;
    const discountAmount = subtotal * (form.value.discount / 100);
    return subtotal - discountAmount;
});

// Function to handle saving the temporary item
const saveTempItem = async () => {
    try {
        isLoading.value = true;

        const projectId = projectStore.currentProjectId;
        const equipmentListId = projectStore.currentEquipmentListId;
        const parentGroupId = projectStore.currentDraggableItemId;

        if (!projectId || !equipmentListId) {
            throw new Error('Project ID or Equipment List ID not found');
        }
        
        // Prepare the data according to the API specification
        const contentData = {
            type: "item",
            asset_type: "temporary",  // Try different asset type
            name: form.value.name,
            quantity: form.value.quantity,
            price: form.value.price,
            days: form.value.days,
            discount: form.value.discount,
            parent_id: parentGroupId ? parentGroupId.toString() : null
        };

        await addEquipmentListContent(projectId, equipmentListId, contentData);

        // Refresh the equipment list content to show the new item
        if (projectStore.refreshFunction && typeof projectStore.refreshFunction === 'function') {
            await projectStore.refreshFunction();
        }
        
        // Close the modal first
        projectStore.isModalVisible = false;
        
        // Reset the form
        form.value = {
            name: '',
            quantity: 1,
            price: 0,
            days: 3,
            discount: 0
        };
        
        // Show success toast
        toast.add({
            severity: 'success',
            summary: 'Temporary Item Added',
            detail: 'Temporary item has been added to the equipment list',
            life: 3000
        });
        
    } catch (error) {
        console.error('Error adding temporary item:', error);
        
        // Show error toast
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to add temporary item: ${error.message}`,
            life: 3000
        });
    } finally {
        isLoading.value = false;
    }
};
</script>

<style scoped>
/* Add any necessary styles here */
</style>
